import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  GlobalStyles,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches, useGetJourneys } from "src/api/generic";
import {
  useGetOrderSalesReport,
  useGetOrderSalesReportData,
  type OrderSalesReportRow,
} from "src/api/reports";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportDataTable,
  ReportPageHeading,
  ReportSectionCard,
  type ReportTableColumn,
  type ReportTableTreeConfig,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";
import { ReportsPermissions } from "src/constants/permissions";

type OrderSalesReportFilters = {
  startDate: string;
  endDate: string;
  branchIds: number[];
  journeyId: string;
};

type JourneyFilterOption = {
  id: number;
  start_date: string;
  end_date: string;
  reference?: number | string;
};

const parseBranchIds = (sp: URLSearchParams): number[] => {
  const fromList = sp.getAll("branches");
  if (fromList.length > 0) {
    return fromList.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }
  const legacy = sp.get("branch_id");
  if (legacy) {
    const n = Number(legacy);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  return [];
};

const parseOrderSalesFilters = (
  sp: URLSearchParams
): OrderSalesReportFilters => ({
  startDate:
    sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
  endDate: sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
  branchIds: parseBranchIds(sp),
  journeyId: sp.get("journey_id") || "",
});

const formatReportAmount = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const journeyOverlapsRange = (
  journey: JourneyFilterOption,
  startDate: string,
  endDate: string
) => {
  return (
    moment(journey.start_date).isSameOrBefore(endDate, "day") &&
    moment(journey.end_date).isSameOrAfter(startDate, "day")
  );
};

const journeyReferenceLabel = (journey: JourneyFilterOption) => {
  const ref = journey.reference;
  if (ref === undefined || ref === null || ref === "") {
    return `${journey.start_date} - ${journey.end_date}`;
  }
  return `S-${ref}`;
};

const OrderSalesReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<OrderSalesReportFilters>(
    () => parseOrderSalesFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<OrderSalesReportFilters>(
    () => parseOrderSalesFilters(new URLSearchParams(searchParams.toString()))
  );

  const { startDate, endDate, branchIds, journeyId } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission(
        ReportsPermissions.viewThirdEyeReportSales,
        branch.id
      )
    ) || [];

  const journeyFilterBranches =
    draftFilters.branchIds.length > 0
      ? draftFilters.branchIds
      : branchesHavePermission.map((branch) => branch.id);

  const { data: journeysBody } = useGetJourneys({
    branches: journeyFilterBranches,
    enabled: journeyFilterBranches.length > 0,
  });

  const journeyOptions = useMemo(() => {
    const raw = (journeysBody as { data?: { journeys?: JourneyFilterOption[] } })
      ?.data?.journeys;
    const list = Array.isArray(raw) ? raw : [];
    return list
      .filter((journey) =>
        journeyOverlapsRange(
          journey,
          draftFilters.startDate,
          draftFilters.endDate
        )
      )
      .sort((a, b) => Number(b.id) - Number(a.id));
  }, [journeysBody, draftFilters.startDate, draftFilters.endDate]);

  const branchFilter = branchIds.filter((id) => Number.isFinite(id) && id > 0);

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetOrderSalesReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchFilter.length > 0 ? branchFilter : undefined,
    journey_id: journeyId || undefined,
    enabled: false,
  });

  const { refetch: refetchDownload, isFetching: isDownloadingReport } =
    useGetOrderSalesReport({
      start_date: startDate,
      end_date: endDate,
      branches: branchFilter.length > 0 ? branchFilter : undefined,
      journey_id: journeyId || undefined,
      enabled: false,
    });

  const rows = useMemo(
    () =>
      (
        reportResponse as { data?: { rows?: OrderSalesReportRow[] } } | undefined
      )?.data?.rows ?? [],
    [reportResponse]
  );

  const syncAppliedToUrl = useCallback(
    (next: OrderSalesReportFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      if (next.branchIds.length > 0) {
        next.branchIds.forEach((id) => p.append("branches", String(id)));
      }
      if (next.journeyId) p.set("journey_id", next.journeyId);
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const [isApplyingReport, setIsApplyingReport] = useState(false);
  const fetchAfterApplyRef = useRef(false);

  useEffect(() => {
    if (!fetchAfterApplyRef.current) {
      return;
    }
    fetchAfterApplyRef.current = false;
    void refetchReport().finally(() => setIsApplyingReport(false));
  }, [appliedFilters, refetchReport]);

  const handleReportUpdate = () => {
    fetchAfterApplyRef.current = true;
    setIsApplyingReport(true);
    setAppliedFilters({ ...draftFilters });
    syncAppliedToUrl(draftFilters);
  };

  const downloadReportsPermission = oneBranchHasPermission(
    ReportsPermissions.viewThirdEyeReportSales
  );

  const periodLabel = useMemo(() => {
    return t("reportDateRange", {
      start: moment(startDate).format("D MMM YYYY"),
      end: moment(endDate).format("D MMM YYYY"),
    });
  }, [t, startDate, endDate]);

  const handleDownload = useCallback(async () => {
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "order-sales-report");
    }
  }, [refetchDownload]);

  const handlePrint = () => {
    window.print();
  };

  const tree: ReportTableTreeConfig<OrderSalesReportRow> = useMemo(
    () => ({
      getKey: (row) => row.key,
      getParentKey: (row) => row.parent_key,
      isExpandable: (row) => row.is_expandable,
      getDepth: (row) => row.depth,
      startCollapsed: true,
      expandColumnId: "name",
    }),
    []
  );

  const columns: ReportTableColumn<OrderSalesReportRow>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("productName"),
        cell: (row) => (
          <Typography
            component="span"
            dir="auto"
            fontWeight={row.is_expandable ? 600 : 400}
            color={row.row_kind === "modifier" ? "#667085" : "#101828"}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        id: "sku",
        header: t("sku"),
        cell: (row) => row.sku || "—",
      },
      {
        id: "order_type",
        header: t("orderTypeColumn"),
        cell: (row) => row.order_type,
      },
      {
        id: "net_quantity",
        header: t("netQuantity"),
        align: "right",
        cell: (row) => (
          <>
            {new Intl.NumberFormat(undefined, {
              maximumFractionDigits: 0,
            }).format(Math.round(row.net_quantity))}
          </>
        ),
      },
      {
        id: "net_sales",
        header: t("netSales"),
        align: "right",
        cell: (row) => (
          <Box component="span" fontWeight={600}>
            {formatReportAmount(row.net_sales)}
          </Box>
        ),
      },
      {
        id: "cost",
        header: generalT("cost"),
        align: "right",
        cell: (row) => <>{formatReportAmount(row.cost)}</>,
      },
      {
        id: "cogs_percent",
        header: t("cogsPercent"),
        align: "right",
        cell: (row) => <>{formatReportAmount(row.cogs_percent)}%</>,
      },
    ],
    [t, generalT]
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#order-sales-print-root, #order-sales-print-root *": {
              visibility: "visible",
            },
            "#order-sales-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("orderSalesReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <FormControl
                size="small"
                sx={{
                  minWidth: 220,
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  "& .MuiOutlinedInput-root": { minHeight: 40 },
                }}
              >
                <InputLabel id="order-sales-branch" shrink>
                  {generalT("branch")}
                </InputLabel>
                <Select<string[]>
                  labelId="order-sales-branch"
                  multiple
                  displayEmpty
                  value={draftFilters.branchIds.map((id) => String(id))}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const v = e.target.value;
                    const next = typeof v === "string" ? v.split(",") : v;
                    const strs = next as string[];
                    if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
                      setDraftFilters((p) => ({
                        ...p,
                        branchIds: [],
                        journeyId: "",
                      }));
                      return;
                    }
                    const ids = strs.map(Number).filter((n) => n > 0);
                    setDraftFilters((p) => ({
                      ...p,
                      branchIds: ids,
                      journeyId: "",
                    }));
                  }}
                  input={<OutlinedInput label={generalT("branch")} />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return t("allBranches");
                    return selected
                      .map(
                        (idStr) =>
                          branchesHavePermission.find(
                            (b) => String(b.id) === idStr
                          )?.name ?? idStr
                      )
                      .join(", ");
                  }}
                >
                  <MenuItem
                    key="__all-branches-order-sales"
                    value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
                    sx={{
                      borderBottom: "1px solid #EAECF0",
                      bgcolor: "#F9FAFB",
                    }}
                  >
                    <Typography fontWeight={600} variant="body2" component="span">
                      {t("allBranches")}
                    </Typography>
                  </MenuItem>
                  {branchesHavePermission.map((branch) => (
                    <MenuItem key={branch.id} value={String(branch.id)}>
                      <Checkbox
                        size="small"
                        checked={draftFilters.branchIds.includes(branch.id)}
                      />
                      <ListItemText primary={branch.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="order-sales-journey" shrink>
                  {t("sale")}
                </InputLabel>
                <Select
                  labelId="order-sales-journey"
                  label={t("sale")}
                  value={draftFilters.journeyId}
                  onChange={(e) => {
                    const v = e.target.value as string;
                    setDraftFilters((p) => ({ ...p, journeyId: v }));
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t("allSales")}</em>
                  </MenuItem>
                  {draftFilters.journeyId &&
                    !journeyOptions.some(
                      (journey) => String(journey.id) === draftFilters.journeyId
                    ) && (
                      <MenuItem value={draftFilters.journeyId}>
                        {t("sale")}
                      </MenuItem>
                    )}
                  {journeyOptions.map((journey) => (
                    <MenuItem key={journey.id} value={String(journey.id)}>
                      {journeyReferenceLabel(journey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ minWidth: 250 }}>
                <DateRangeInput
                  startDate={
                    draftFilters.startDate
                      ? moment(draftFilters.startDate)
                      : null
                  }
                  startDateId="order-sales-start"
                  endDate={
                    draftFilters.endDate ? moment(draftFilters.endDate) : null
                  }
                  endDateId="order-sales-end"
                  onDatesChange={(arg: {
                    startDate: moment.Moment | null;
                    endDate: moment.Moment | null;
                  }) => {
                    const s = arg.startDate?.format("yyyy-MM-DD");
                    const e = arg.endDate?.format("yyyy-MM-DD");
                    if (s && e) {
                      setDraftFilters((p) => ({
                        ...p,
                        startDate: s,
                        endDate: e,
                        journeyId: "",
                      }));
                    }
                  }}
                />
              </Box>
            </>
          }
          center={
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleReportUpdate}
              disabled={isFetching || isApplyingReport}
              sx={{ height: 40, minWidth: 120 }}
            >
              {t("reportUpdate")}
            </Button>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadingReport}
                disabled={!downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Box id="order-sales-print-root">
        <ReportPageHeading
          title={t("orderSalesReport")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          <ReportDataTable<OrderSalesReportRow>
            columns={columns}
            rows={rows}
            getRowId={(row) => row.key}
            tree={tree}
            isLoading={isFetching || isApplyingReport}
            emptyMessage={
              <Typography color="text.secondary" align="center" py={2}>
                {generalT("noData")}
              </Typography>
            }
          />
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default OrderSalesReportPage;
